import { Box, Typography } from '@mui/material';
import type { HostedReport } from '../types/report.types';
import { BrandMark } from './AgencyBar';
import { formatMonthDayYear } from '../reportDate';
import {
  CARD_BORDER,
  FAINT_FG,
  HEADING_FG,
  MUTED_FG,
  WORK_RULE_STRONG,
} from '../reportTokens';

/**
 * The design's footer: the agency's mark, a line back to whoever sent this,
 * and the "data through" date.
 *
 * The contact sentence links to `branding.contactEmail` — the resolved
 * sender identity's reply address (see `sendReport` in
 * `services/ReportService.js`) — when the snapshot carries one. Absent on a
 * v2 snapshot, and null when resolution found no address; either way the
 * fallback below points back at the email itself rather than fabricating an
 * address, the way the design's own placeholder `hello@agency.com` would.
 */
function ReportFooter({ report }: { report: HostedReport }) {
  const dataThrough = formatMonthDayYear(report.period.end) || '';
  const contactEmail = report.branding?.contactEmail;
  return (
    <Box
      sx={{
        borderTop: `1px solid ${CARD_BORDER}`,
        mt: '8px',
        pt: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        {/* The mark and the name are one unit, tighter than the gap to the
            sentence beside them — otherwise the name floats between the two
            and reads as part of the sentence. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BrandMark report={report} size={26} />
          {report.branding?.brandName && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: HEADING_FG,
              }}
            >
              {report.branding.brandName}
            </Typography>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: '13px',
            color: MUTED_FG,
            lineHeight: 1.6,
            minWidth: 0,
          }}
        >
          {contactEmail ? (
            <>
              Questions about this report? Reply to the email or contact{' '}
              <Box
                component='a'
                href={`mailto:${contactEmail}`}
                sx={{ color: WORK_RULE_STRONG, fontWeight: 600 }}
              >
                {contactEmail}
              </Box>
            </>
          ) : (
            'Questions about this report? Reply to the email it arrived in.'
          )}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '12px', color: FAINT_FG }}>
        {dataThrough ? `Data through ${dataThrough}.` : 'Prepared for you.'}
      </Typography>
    </Box>
  );
}

export default ReportFooter;
